import _ from 'lodash';
import React from 'react';

import {
  SaveTriageWorkflowInput,
  StoreListWidgetDocument,
  StoreTypeName,
  TriageWorkflowTypeName,
  useSaveStoreMutation,
  SalesChannelName
} from 'generated/graphql';

import * as types from './types';



export function useSaveStore() {
  const [fnMutate, tuple] = useSaveStoreMutation({
    refetchQueries: [
      {
        query: StoreListWidgetDocument,
        variables: {}
      }
    ]
  });

  const fnWrappedMutate = React.useCallback(
    async (formState: types.IFormState, storeTypeName: StoreTypeName) => {
      await fnMutate({
        variables: {
          input: {
            settings: {
              ignoresSkuSuffixes: true,
              krogerPlanSKUs: [],
              writesDiscountLine: false,
              writesToCatalyst: false,
              writesToShipStation: true,
              prefixNewOrders: false
            },
            name: formState.name,
            orderPrefix: formState.orderPrefix,
            type: __getStoreTypeName(storeTypeName, formState.salesChannel.id),
            avaTaxCompanyCode: formState.avaTaxCompanyCode,

            wooCommerceMeta: storeTypeName === 'wooCommerce' ? {
              baseUrl: formState.baseUrl,
              consumerKey: formState.wooCommerceKey,
              consumerSecret: formState.wooCommerceSecret
            } : undefined,

            shopifyMeta: storeTypeName === 'shopify' ? {
              baseUrl: formState.baseUrl,
              adminToken: formState.shopifyAdminKey
            } : undefined,

            telgoo5Meta: storeTypeName === 'telgoo5' ? {
              customerId: formState.telgoo5CustomerId,
              partnerName: formState.telgoo5PartnerName,
              sourceUrl: formState.telgoo5SourceUrl,
              apiUrl: formState.telgoo5ApiUrl,
              allowableIPAddresses: _.map(formState.clientIdentifiers, id => id.value)
            } : undefined,

            salesChannelName: formState.salesChannel.id,
            brandId: formState.netSuiteBrand.id,
            inventoryLocationId: formState.netSuiteLocation.id,
            departmentId: formState.netSuiteDepartment.id,
            subsidiaryId: formState.netSuiteSubsidiary.id,
            taxItemId: formState.netSuiteTaxItem.id,
            netSuiteClientInternalId: formState.netSuiteClientInternalId || undefined,
            sftpMeta: storeTypeName === 'magicJack' ? {
              baseUrl: formState.sftpHost,
              username: formState.sftpUsername,
              password: formState.sftpPassword,
              orderFileDirectory: formState.sftpOrderFileDirectory,
              ackFileDirectory: formState.sftpAckFileDirectory,
              shipFileDirectory: formState.sftpShipFileDirectory
            } : undefined,
            triageWorkflows: storeTypeName === 'cleoTier1' ? _.filter([
              __formWorkflow2GqlInput(formState.triageWorkflow, 'triage'),
              __formWorkflow2GqlInput(formState.receivingWorkflow, 'receiving'),
              __formWorkflow2GqlInput(formState.replacementWorkflow, 'replacement'),
              __formWorkflow2GqlInput(formState.repairWorkflow, 'restock'),
              __formWorkflow2GqlInput(formState.returnWorkflow, 'return'),
              __formWorkflow2GqlInput(formState.creditWorkflow, 'credit'),
              __formWorkflow2GqlInput(formState.scrapWorkflow, 'scrap')
            ], wf => wf.isEnabled) : undefined
          }
        }
      });
    },
    [
      fnMutate
    ]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}



function __formWorkflow2GqlInput(formState: types.ITriageWorkflow, triageWorkflowTypeName: TriageWorkflowTypeName): SaveTriageWorkflowInput {
  return {
    isEnabled: formState.enabled === 'true',
    name: formState.name,
    netSuiteBinInternalId: formState.binId,
    triageWorkflowTypeName
  };
}

function __getStoreTypeName(storeTypeName: StoreTypeName, salesChannelName: SalesChannelName): StoreTypeName {
  return storeTypeName === 'shopify' && salesChannelName === 'ecommerce' ? 'shopify_ecommerce' : storeTypeName;
}
